/**
 * Weight helpers.
 *
 * The counter enters grams and milligrams in two boxes, as specified. Underneath,
 * both collapse into a single DECIMAL(12,3) in grams — storing two integer columns
 * would turn every SUM(), rate multiplication and stock report into a carry-over
 * bug waiting to happen.
 */
import Decimal from 'decimal.js';
import { money } from './money.js';

/** Milligram precision. Nothing in the trade is weighed finer than this. */
const DP = 3;

export function weight(value) {
  return money(value);
}

export function round3(value) {
  return money(value).toDecimalPlaces(DP, Decimal.ROUND_HALF_UP);
}

/** Serialise for a DECIMAL(12,3) column. Always "10.500", never 10.5. */
export function toWeight(value) {
  return round3(value).toFixed(DP);
}

/** Two counter inputs -> one stored weight in grams. */
export function fromGramsMilligrams(grams, milligrams) {
  const mg = Math.trunc(Number(milligrams) || 0);
  if (mg < 0 || mg > 999) throw new RangeError(`Milligrams must be 0-999, got ${milligrams}`);
  return round3(money(grams).plus(new Decimal(mg).dividedBy(1000)));
}

/** One stored weight -> two counter inputs. */
export function toGramsMilligrams(value) {
  const v = round3(value);
  const grams = v.toDecimalPlaces(0, Decimal.ROUND_DOWN);
  return { grams: grams.toNumber(), milligrams: v.minus(grams).times(1000).round().toNumber() };
}

/**
 * Fine (pure) gold content. 10 g of 22K at 0.9160 fineness is 9.160 g fine.
 * Used by stock valuation and by old-gold buy-back rates.
 */
export function fineWeight(grossOrNetWeight, fineness) {
  return round3(money(grossOrNetWeight).times(money(fineness)));
}

export function sumWeights(values) {
  return round3(values.reduce((acc, v) => acc.plus(money(v)), new Decimal(0)));
}

/** Display form: "10.500 g". Weights are always shown to milligram precision. */
export function formatWeight(value, { unit = 'g' } = {}) {
  return `${round3(value).toFixed(DP)}${unit ? ` ${unit}` : ''}`;
}

/**
 * Default tolerance for the issue/receipt weight-mismatch alert, in grams.
 * Configurable per shop; 50 mg is a sensible starting point for karigar returns.
 */
export const DEFAULT_WEIGHT_TOLERANCE = '0.050';
